/* eslint-disable @typescript-eslint/naming-convention */
import {
  call,
  fork,
  put,
  take,
  takeEvery,
  takeLatest,
  select,
} from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { initialize } from 'redux-form';
import forEach from 'lodash/forEach';
import find from 'lodash/find';
import moment from 'moment';
import Store from 'store';

import {
  OUTLET_PAGE_ASK_REACTIVATE_OUTLET_WITHOUT_PAY,
  OUTLET_PAGE_CONFIRM_REACTIVATE_OUTLET_WITHOUT_PAY,
  OUTLET_PAGE_LIST_ASK_REACTIVATE_OUTLET,
  OUTLET_PAGE_LIST_CANCEL_REACTIVATE_OUTLET,
  OUTLET_PAGE_LIST_CLICK_ADD_OUTLET,
  OUTLET_PAGE_LIST_CLICK_CANCEL,
  OUTLET_PAGE_LIST_CLICK_CREATE_OUTLET,
  OUTLET_PAGE_LIST_CLICK_OUTLET_ROW,
  OUTLET_PAGE_LIST_CONFIRM_REACTIVATE_OUTLET,
  OUTLET_PAGE_LIST_GET_BILLING,
  OUTLET_PAGE_LIST_GET_OUTLET_PRICE,
  OUTLET_PAGE_LIST_GET_SUBSCRIPTION_STATUS,
  OUTLET_PAGE_LIST_PAGE_REQUEST,
  OUTLET_PAGE_LIST_SEARCH,
  OUTLET_PAGE_LIST_SUBMIT_FORM_CREATE,
  OUTLET_PAGE_LIST_SUBMIT_FORM_UPDATE,
  OUTLET_PAGE_LIST_VERIFY_ADD_OUTLET_BILLING_DATA,
  OUTLET_PAGES_IS_STILL_CLONING,
  OUTLET_PAGES_SUBMIT_CLONE_OUTLET,
  OUTLET_PAGES_CHECK_CLONE_ON_PROGRESS,
  OUTLET_PAGES_DELETE_CLONE_CACHE_ON_SUCCESS,
  OUTLET_PAGES_CHECK_CLONE_PROGRESS_CHANGED,
  OUTLET_PAGES_POPULATE_CLONE_OUTLET_DATA,
  OUTLET_PAGES_CLONE_SUCCESS_AND_CONFIRMED,
  OUTLET_PAGES_ON_CHANGE_STATUS,
  OUTLET_PAGES_GOBIZ_OUTLET_LINKING_CONFIRMED,
  OUTLET_PAGES_PREPARE_GOBIZ_OUTLET_LINKING,
  OUTLET_PAGE_LIST_CLICK_OUTLET_DETAIL_ROW,
  OUTLET_PAGES_GOBIZ_OUTLET_RECOMMENDATION_ON_CHANGE_OUTLET,
  OUTLET_PAGE_LIST_UNLINK_GOBIZ_MODAL,
} from '../../actions/outletPages/list/constants';
import * as actOutletList from '../../actions/outletPages/list';
import * as actNotif from '../../actions/notifications';
import * as act from '../../actions';
import {
  setNotificationMessage,
  setNotificationType,
} from '../../actions/billingRedirect';
import * as actItem from '../../actions/library/item';
import { showBillingNotification } from '../../actions/billingNotification';
import { showPageLoadingSpinner } from '../../actions/loadingSpinner';
import { actSetAccountBusinessLocation } from '../../actions/setting/account';
import {
  resetKybCityDropdown,
  resetKybKecamatanDropdown,
  resetKybZipcodeDropdown,
} from '../../actions/kyb';

import * as apiProfile from '../../api/profile';
import * as apiOutlet from '../../api/outlets';
import * as apiBilling from '../../api/billing';
import { getSaudagarOutletDetail } from '../../api/gobiz';
import * as apiPaymentMethod from '../../api/paymentMethods';
import * as apiCloneOutlet from '../../api/inventory/cloneOutlet/cloneoutlet';
import {
  deletePartnerToken,
  exchangeAuthCode,
  upsertPartnerToken,
} from '../../api/partner/partner';
import { REDIRECT_GOBIZ_URL } from '../../api/constants';
import {
  phoneNumberFormatter,
  randomString,
  useMokaOutletPropsFirst,
} from '../../utils/formatter';
import { getSelectedOutlet } from '../../utils/userProperties';
import { getSelectedOutletIdFromLocalStorage } from '../../utils/selectedOutletId';
import { assertErrorHasMessage, assertErrorHasData } from '../../utils/assert';
import * as clevertapOutletsList from '../../clevertap/outlets/list';

function* openRightColumn() {
  yield put(actOutletList.openRightColumn());
}

function* closeRightColumn() {
  yield put(actOutletList.closeRightColumn());
}

function* fillForm(outlet) {
  const form = {
    id: outlet.id,
    businessId: outlet.business_id,
    name: useMokaOutletPropsFirst(outlet, (o) => o.name),
    address: useMokaOutletPropsFirst(outlet, (o) => o.address),
    postal: useMokaOutletPropsFirst(outlet, (o) => o.postal_code),
    city: useMokaOutletPropsFirst(outlet, (o) => o.city),
    kecamatan: useMokaOutletPropsFirst(outlet, (o) => o.kecamatan),
    province: useMokaOutletPropsFirst(outlet, (o) => o.province),
    phone: useMokaOutletPropsFirst(outlet, (o) =>
      !o.phone_number ? null : phoneNumberFormatter(o.phone_number)
    ),
    outletToggle: outlet.isCloneOutlet,
    clonedOutlet: !outlet.source_clone_id ? null : outlet.source_clone_id,
  };
  yield put(initialize('outlet', form));
}

function* fillSearchFormFromOutletIds(outletIds) {
  let assignedOutletIds: any[] = [];
  if (outletIds.length > 1) {
    assignedOutletIds = [outletIds[0]];
  } else {
    assignedOutletIds = outletIds;
  }
  const data = { outletIds: assignedOutletIds };
  yield put(initialize('search', data));
}

function* isCloningEligible() {
  const isEligible = false;
  try {
    const response = yield call(apiCloneOutlet.isEligible);
    if (response && response.status === 200) {
      return true;
    }
  } catch (error) {
    return isEligible;
  }
  return isEligible;
}

function* processCloneOutlet() {
  try {
    // check is business is eligible for cloning outlet
    const isEligible = yield call(isCloningEligible);
    if (isEligible) {
      yield put(actOutletList.setCloningEligible());
      yield put(actOutletList.isCloning());
    }
    yield put(actOutletList.populateCloneOutletData());
  } catch (error) {
    yield put(actOutletList.setCloningForbidden());
  }
}

function* onCloningInProgress(params) {
  const message = `Cloning from outlet ${params.source} to outlet ${params.destination} is in progress. Please do not make any changes on ${params.source}.`;
  const targetLocation = '/outlets/list';
  const notification = {
    level: 'info',
    message,
    closeable: false,
    targetLocation,
  };
  yield put(actOutletList.showCloneOutletNotification(notification));
  yield put(actNotif.persistentInfo({ message, targetLocation }));
}

function* onCloningCompleted(params) {
  const message = `Cloning from ${params.source} to ${params.destination} is completed. Please check your new outlet.`;
  const targetLocation = '/outlets/list';
  const notification = {
    level: 'clone-success',
    message,
    closeable: false,
    targetLocation,
  };
  yield put(actOutletList.showCloneOutletNotification(notification));
  yield put(actNotif.persistentSuccess({ message, targetLocation }));
}

function* onCloningCompletedAndConfirmed(params) {
  const { destination_outlet_id } = params.payload.data;
  yield put(actOutletList.deleteCloneCacheOnSuccess());
  yield put(actOutletList.resetCloningProgress());
  yield put(actOutletList.setCloneOutletId(destination_outlet_id));
  const arr: number[] = [];
  arr.push(parseInt(destination_outlet_id, 10));
  yield put(actItem.onChangeOutletIds(arr));
}

function* onCloningError(params) {
  const message = `Cloning from ${params.source} to ${params.destination} failed. Click here to go back to outlet setting.`;
  const targetLocation = '/outlets/list';
  const notification = {
    level: 'error',
    message,
    closeable: false,
    targetLocation,
  };
  yield put(actOutletList.showCloneOutletNotification(notification));
  yield put(actNotif.persistentError({ message, targetLocation }));
}

function* prepareGoBizOutletLinking(request) {
  try {
    const { authCode, error } = request.payload;
    if (error) {
      throw new Error(error);
    }
    const respExchangeAuthCode = yield call(exchangeAuthCode, authCode);
    const respSaudagarOutletDetail = yield call(
      getSaudagarOutletDetail,
      respExchangeAuthCode.data.access_token
    );
    const mokaOutletInfo = Store.get('selectedOutletToLinkGoBiz');
    yield put(
      actOutletList.setGoBizLinkingMerchantInfo(
        mokaOutletInfo,
        respSaudagarOutletDetail.data.hits[0]
      )
    );
    Store.set('response:exchangeAuthCode', respExchangeAuthCode.data);
  } catch (error) {
    yield put(
      actNotif.error(
        'Unable to continue GoBiz outlet linking process due to technical error. Please try again later.'
      )
    );
  }
}

function* processGoBizOutletLinking(request) {
  try {
    const { saudagar, outlets } = request.payload;
    const goBizAccessTokenData = Store.get('response:exchangeAuthCode');
    const selectedOutletData = Store.get('selectedOutletToLinkGoBiz');
    yield call(upsertPartnerToken, selectedOutletData.id, goBizAccessTokenData);

    let outlet;
    let retry = 1;
    do {
      yield call(delay, 1000);
      const res = yield call(apiOutlet.getOutletById, selectedOutletData.id, {
        refresh: true,
      });
      outlet = res.data;
      retry += 1;
    } while (outlet?.saudagar?.status !== 'active' && retry < 6);
    if (!outlet.saudagar) {
      throw new Error('Server error. please try again');
    }
    outlets.forEach((o, idx) => {
      if (o.id === outlet.id) {
        outlets[idx] = outlet;
      }
    });

    yield put(
      actNotif.success(
        `Linking successful. GoBiz outlet ${saudagar.merchant_name} has been successfully linked to Moka.`
      )
    );

    yield put(actOutletList.setOutlets(outlets));
    yield put(actOutletList.setOutletDetail(outlet));
    yield put(
      actSetAccountBusinessLocation({
        province: useMokaOutletPropsFirst(outlet, (o) => o.province),
        city: useMokaOutletPropsFirst(outlet, (o) => o.city),
        kecamatan: useMokaOutletPropsFirst(outlet, (o) => o.kecamatan),
      })
    );
    yield call(fillForm, outlet);
    yield call(openRightColumn);
    yield put(actOutletList.showCloneOutletOption(false));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    Store.remove('response:exchangeAuthCode');
    Store.remove('selectedOutletToLinkGoBiz');
    yield put(actOutletList.hideGoBizOutletLinkingModal());
    yield put(act.sendingRequest(false));
    yield put(act.hideProgressBar());
  }
}

function* unlinkGoBizLinking(request) {
  const { outletId, outlets } = request.payload;
  yield put(actOutletList.hideUnlinkGoBizModal());
  yield put(act.sendingRequest(true));
  yield put(act.showProgressBar());
  try {
    yield call(deletePartnerToken, outletId);

    let outlet;
    let retry = 1;
    do {
      yield call(delay, 1000);
      const res = yield call(apiOutlet.getOutletById, outletId, {
        refresh: true,
      });
      outlet = res.data;
      retry += 1;
    } while (outlet.saudagar && retry < 6);
    if (outlet.saudagar) {
      throw new Error('Server error. please try again');
    }
    outlets.forEach((o, idx) => {
      if (o.id === outlet.id) {
        outlets[idx] = outlet;
      }
    });

    yield put(actOutletList.setOutlets(outlets));
    yield put(actOutletList.setOutletDetail(null));
    yield call(closeRightColumn);
    yield put(actNotif.success(`Unlink successful`));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actOutletList.hideUnlinkGoBizModal());
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.sendingRequest(false));
    yield put(act.hideProgressBar());
  }
}

function* unlinkCurrentGoBizLinkingAndRedirect(request) {
  try {
    const { outlet } = request.payload;
    Store.set('selectedOutletToLinkGoBiz', outlet);
    yield call(deletePartnerToken, outlet.id);
  } catch (error) {
    // Unable to unlink current GoBiz outlet. However the linking process can continue.
  } finally {
    window.location.assign(REDIRECT_GOBIZ_URL(randomString(16)));
  }
}

function* populateCloneOutletData() {
  try {
    const resOutlet = yield call(apiOutlet.outlets, { with_inactive: false });
    yield put(actOutletList.setCloneOutletData(resOutlet.data));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  }
}

export function* setUsedAndTotalOutletSlots(profile, outlets) {
  // count outlets
  const outletSlots = profile.business.outlet_slot;
  let usedOutletSlots = 0;
  forEach(outlets, (outlet) => {
    const endDate = moment(outlet.end_date);
    const todayDate = moment();
    if (endDate.isSameOrAfter(todayDate)) usedOutletSlots++;
  });

  yield put(actOutletList.setOutletSlots(outletSlots));
  yield put(actOutletList.setUsedOutletSlots(usedOutletSlots));
}

export function* getBillingsFlow() {
  yield put(act.sendingRequest(true));
  yield put(act.showProgressBar());

  const response = yield call(apiBilling.getBillings);
  let pendingPaymentMessage: string | null = null;
  forEach(response.data, (invoice) => {
    if (invoice.status === 'pending') {
      if (invoice.payment_type === 'credit_card') {
        pendingPaymentMessage = `There is a pending payment for ${invoice.invoice_number}. Please wait 24 hours before making a new payment.`;
      } else if (
        invoice.payment_type === 'bank_transfer' ||
        invoice.payment_type === 'echannel'
      ) {
        pendingPaymentMessage = `There is a pending payment for ${invoice.invoice_number}. Please transfer full amount of the invoice within 3 days before making a new payment.`;
      }
      return false;
    }
  });
  if (pendingPaymentMessage !== null) {
    yield put(actOutletList.setPendingPaymentMessage(pendingPaymentMessage));
    yield put(actOutletList.disablePayButton());
  }
  yield put(act.sendingRequest(false));
  yield put(act.hideProgressBar());
}

export function* pageRequest(request) {
  clevertapOutletsList.outletListStart();
  // reset pages
  yield put(actOutletList.setOutletDetail(null));
  yield put(actOutletList.setOutlets([]));
  yield put(showPageLoadingSpinner(true));
  yield put(actOutletList.setUsedOutletSlots(null));
  yield put(actOutletList.setOutletSlots(null));
  yield call(closeRightColumn);

  try {
    yield put(act.sendingRequest(true));
    yield put(act.showProgressBar());

    const resp = yield call(apiOutlet.getOutlets, { with_inactive: true });
    const resOutlet = resp.data;

    yield put(actOutletList.setOutlets(resOutlet.data));

    const res = yield call(apiProfile.getProfile);
    yield put(actOutletList.setProfile(res.data));
    yield call(
      fillSearchFormFromOutletIds,
      getSelectedOutletIdFromLocalStorage()
    );
    yield call(processCloneOutlet);

    yield put(actOutletList.getOutletPrice());
    yield put(actOutletList.getFeatureSubscriptionStatus());
    yield put(actOutletList.getBilling());
    yield put(showBillingNotification(request.payload.billingNotification));
    yield put(setNotificationType(null));
    yield put(setNotificationMessage(null));

    yield call(setUsedAndTotalOutletSlots, res.data, resOutlet.data);

    const responsePaymentMethod = yield call(
      apiPaymentMethod.getAllPaymentMethods
    );
    yield put(actOutletList.setPaymentMethodsList(responsePaymentMethod.data));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.sendingRequest(false));
    yield put(act.hideProgressBar());
    yield put(showPageLoadingSpinner(false));
  }
}

export function* clickCreateOutlet() {
  // noinspection InfiniteLoopJS
  while (true) {
    yield take(OUTLET_PAGE_LIST_CLICK_CREATE_OUTLET);
    try {
      yield put(act.sendingRequest(true));
      yield put(act.showProgressBar());

      const res = yield call(apiOutlet.getNew, {});
      yield put(actOutletList.setOutletDetail(null));
      yield put(act.businessProvince());
      yield put(resetKybCityDropdown());
      yield put(resetKybKecamatanDropdown());
      yield put(resetKybZipcodeDropdown());
      yield call(fillForm, res.data);
      yield call(openRightColumn);
      yield put(actOutletList.showCloneOutletOption(true));
    } catch (error) {
      assertErrorHasMessage(error);
      yield put(actNotif.error(error.message));
    } finally {
      yield put(act.sendingRequest(false));
      yield put(act.hideProgressBar());
    }
  }
}

export function* clickAddOutlet() {
  // noinspection InfiniteLoopJS
  while (true) {
    yield take(OUTLET_PAGE_LIST_CLICK_ADD_OUTLET);
    yield put(actOutletList.showAddOutletModal());
  }
}

export function* clickOutletRow() {
  // noinspection InfiniteLoopJS
  while (true) {
    const {
      payload: { outletId },
    } = yield take(OUTLET_PAGE_LIST_CLICK_OUTLET_ROW);
    try {
      yield put(act.sendingRequest(true));
      yield put(act.showProgressBar());

      const res = yield call(apiOutlet.getOutletById, outletId);
      const outlet = res.data;
      yield put(actOutletList.setOutletDetail(outlet));
      yield put(
        actSetAccountBusinessLocation({
          province: useMokaOutletPropsFirst(outlet, (o) => o.province),
          city: useMokaOutletPropsFirst(outlet, (o) => o.city),
          kecamatan: useMokaOutletPropsFirst(outlet, (o) => o.kecamatan),
        })
      );
      yield call(fillForm, outlet);
      yield call(openRightColumn);
      yield put(actOutletList.showCloneOutletOption(false));
    } catch (error) {
      assertErrorHasMessage(error);
      yield put(actNotif.error(error.message));
    } finally {
      yield put(act.sendingRequest(false));
      yield put(act.hideProgressBar());
    }
  }
}

export function* clickOutletDetailRow() {
  // noinspection InfiniteLoopJS
  while (true) {
    const {
      payload: { outlet },
    } = yield take(OUTLET_PAGE_LIST_CLICK_OUTLET_DETAIL_ROW);
    try {
      yield put(act.sendingRequest(true));
      yield put(act.showProgressBar());

      yield put(actOutletList.setOutletDetail(outlet));
      yield put(
        actSetAccountBusinessLocation({
          province: useMokaOutletPropsFirst(outlet, (o) => o.province),
          city: useMokaOutletPropsFirst(outlet, (o) => o.city),
          kecamatan: useMokaOutletPropsFirst(outlet, (o) => o.kecamatan),
        })
      );
      yield call(fillForm, outlet);
      yield call(openRightColumn);
      yield put(actOutletList.showCloneOutletOption(false));
    } catch (error) {
      assertErrorHasMessage(error);
      yield put(actNotif.error(error.message));
    } finally {
      yield put(act.sendingRequest(false));
      yield put(act.hideProgressBar());
    }
  }
}

export function* clickCancel() {
  // noinspection InfiniteLoopJS
  while (true) {
    yield take(OUTLET_PAGE_LIST_CLICK_CANCEL);
    yield put(actOutletList.setOutletDetail(null));
    yield call(fillForm, {});
    yield call(closeRightColumn);
  }
}

export function* submitFormCreate(action) {
  const {
    payload: { data, previousOutlets, paymentMethodList, isSubscribed },
  } = action;

  if (isSubscribed) {
    let isOutletPaymentMethodNull = false;
    paymentMethodList.forEach((pm) => {
      if (
        pm.outlet_statuses &&
        find(pm.outlet_statuses, (v) => {
          return v === 'APPROVED';
        })
      ) {
        isOutletPaymentMethodNull = true;
      }
    });

    if (isOutletPaymentMethodNull) {
      yield put(actOutletList.setOpenInformationModal(true));
    }
  }

  yield put(actOutletList.setDisableSaveOutletButton(true));
  yield put(act.sendingRequest(true));
  yield put(act.showProgressBar());

  try {
    // call api
    const { isCloneOutlet, source_clone_id, ...createOutletData } = data.outlet;
    const response = yield call(apiOutlet.createOutlet, createOutletData);

    // set to reducers
    const outlet = response.data.data;
    previousOutlets.unshift(outlet);

    yield put(actOutletList.setOutletDetail(outlet));
    yield put(actOutletList.setOutlets(previousOutlets));

    // delay, since there are async process such as deducting outlet slot
    // after create new outlet
    yield call(delay, 2000);

    // call outlets and profile api to get count
    const resProfile = yield call(apiProfile.getProfile);
    const resOutlets = yield call(apiOutlet.outlets);
    yield call(setUsedAndTotalOutletSlots, resProfile.data, resOutlets.data);

    const cloneOutletParam: any = {};
    if (isCloneOutlet) {
      cloneOutletParam.destination_outlet_id = outlet.id;
      cloneOutletParam.source_outlet_id = source_clone_id;
      cloneOutletParam.source_outlet_name = data.outlet.source_clone_name;
      cloneOutletParam.destination_outlet_name =
        data.outlet.destination_outlet_name;

      outlet.isCloneOutlet = isCloneOutlet;
      outlet.source_clone_id = source_clone_id;
      yield put(actOutletList.submitCloneOutlet(cloneOutletParam));
    } else {
      clevertapOutletsList.cloneOutletOff();
      yield put(actOutletList.showCloneOutletOption(false));
      yield put(actNotif.success('Outlet successfully created.'));
    }
    yield call(fillForm, outlet);

    clevertapOutletsList.outletListCreateSuccess({
      outlet,
      loggedInEmail: resProfile.data.email,
    });
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
    yield put(actOutletList.hideCloneOutletProgress());
  } finally {
    yield put(act.sendingRequest(false));
    yield put(act.hideProgressBar());
    yield put(actOutletList.setDisableSaveOutletButton(false));
  }
}

export function* submitFormUpdate(action) {
  const {
    payload: { data, outletId, previousOutlets },
  } = action;
  yield put(act.sendingRequest(true));
  yield put(act.showProgressBar());
  yield put(actOutletList.setDisableSaveOutletButton(true));

  try {
    const response = yield call(apiOutlet.updateOutlet, outletId, data.outlet);
    // set to reducers
    const outlet = response.data.data;
    previousOutlets.forEach((po, idx) => {
      if (po.id === outlet.id) {
        if (previousOutlets[idx].moka) {
          previousOutlets[idx].moka = outlet;
        } else {
          previousOutlets[idx] = outlet;
        }
      }
    });
    yield put(actOutletList.setOutlets(previousOutlets));

    // call outlets and profile api to get count
    const resProfile = yield call(apiProfile.getProfile);
    const resOutlets = yield call(apiOutlet.outlets);
    yield call(setUsedAndTotalOutletSlots, resProfile.data, resOutlets.data);

    yield call(fillForm, outlet);

    yield put(actNotif.success('Outlet was successfully updated.'));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.sendingRequest(false));
    yield put(act.hideProgressBar());
    yield put(actOutletList.setDisableSaveOutletButton(false));
  }
}

export function* search(action) {
  yield put(initialize('outlet', {}));
  yield call(closeRightColumn);
  yield put(actOutletList.setOutlets([]));
  yield put(showPageLoadingSpinner(true));
  try {
    const {
      payload: { keyword, status },
    } = action;
    // prevent too fast ajax call
    yield call(delay, 300);
    yield put(act.sendingRequest(true));
    yield put(act.showProgressBar());

    const resp = yield call(apiOutlet.getOutlets, {
      with_inactive: true,
      keyword,
      status,
    });
    const resOutlet = resp.data;

    yield put(actOutletList.setOutlets(resOutlet.data));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.sendingRequest(false));
    yield put(act.hideProgressBar());
    yield put(showPageLoadingSpinner(false));
  }
}

export function* onChangeStatusFlow(action) {
  yield put(actOutletList.setOutlets([]));
  // @ts-ignore Argument of type '() => Generator<PutEffect<{ type: string; payload: {}; }>, void, unknown>' is not assignable to parameter of type 'Action<any>'.ts(2345)
  yield put(closeRightColumn);
  yield put(showPageLoadingSpinner(true));

  try {
    const {
      payload: { keyword, status },
    } = action;

    yield put(act.sendingRequest(true));
    yield put(act.showProgressBar());

    const resp = yield call(apiOutlet.getOutlets, {
      with_inactive: true,
      keyword,
      status,
    });
    const resOutlet = resp.data;
    yield put(actOutletList.setOutlets(resOutlet.data));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.sendingRequest(false));
    yield put(act.hideProgressBar());
    yield put(showPageLoadingSpinner(false));
  }
}

export function* askReactivateOutletWithoutPay(action) {
  const {
    payload: { outlet },
  } = action;
  yield put(actOutletList.setReactivatedOutlet(outlet));
  yield put(actOutletList.showReactivateOutletWithoutPay());
}

export function* askReactivateOutlet(action) {
  const {
    payload: { outlet },
  } = action;
  yield put(actOutletList.setReactivatedOutlet(outlet));
  yield put(actOutletList.showConfirmReactivateOutletModal());
}

export function* confirmReactivateOutletWithoutPayFlow(action) {
  const {
    payload: { outlet, previousOutlets },
  } = action;
  yield put(actOutletList.setOnLoadingReactivateOutletWithoutPay(true));
  yield put(act.sendingRequest(true));
  yield put(act.showProgressBar());
  try {
    const res = yield call(
      apiOutlet.patchOutletById,
      outlet.id,
      { is_deleted: false },
      {}
    );
    previousOutlets.forEach((po, idx) => {
      if (po.id === res.data.id) previousOutlets[idx] = res.data;
    });
    // call outlets and profile api to get count
    const resProfile = yield call(apiProfile.getProfile);
    const resOutlets = yield call(apiOutlet.outlets);
    yield call(setUsedAndTotalOutletSlots, resProfile.data, resOutlets.data);
    yield put(actOutletList.setOutletDetail(res.data));
    yield put(actOutletList.setOutlets(previousOutlets));
    yield put(actNotif.success('Outlet has been reactivated'));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.sendingRequest(false));
    yield put(act.hideProgressBar());
    yield put(actOutletList.setReactivatedOutlet(null));
    yield put(actOutletList.hideReactivateOutletWithoutPay());
    yield call(closeRightColumn);
    yield put(actOutletList.setOnLoadingReactivateOutletWithoutPay(false));
  }
}

export function* confirmReactivateOutlet(action) {
  const { billingData } = action.payload;
  yield put(act.sendingRequest(true));
  yield put(actOutletList.disablePayButton());
  yield put(actOutletList.setOnLoading(true));
  let response: any = {};
  try {
    response = yield call(apiBilling.verifyBilling, billingData);
  } catch (e) {
    yield put(actOutletList.setReactivatedOutlet(null));
    yield put(actOutletList.hideConfirmReactivateOutletModal());
    yield put(actOutletList.setOnLoading(false));
    yield put(actOutletList.enablePayButton());
    yield put(actOutletList.showPaymentGatewayError());
  } finally {
    yield put(act.sendingRequest(false));
    yield put(act.hideProgressBar());
    if (response.data) {
      yield put(
        actOutletList.setPaymentButtonMessage('Redirecting to Veritrans')
      );
      window.location.replace(response.data.data.redirect_url);
    }
  }
}

export function* cancelReactivateOutlet() {
  yield put(actOutletList.setReactivatedOutlet(null));
  yield put(actOutletList.hideConfirmReactivateOutletModal());
}

export function* outletPriceFlow() {
  yield put(act.sendingRequest(true));
  yield put(act.showProgressBar());

  const response = yield call(apiOutlet.getOutletPrice);
  yield put(actOutletList.setOutletPrice(response.data));
  yield put(
    initialize('AddOutletModalForm', {
      countOutlet: 1,
    })
  );
  yield put(act.sendingRequest(false));
  yield put(act.hideProgressBar());
}

export function* featureSubscriptionStatusFlow() {
  yield put(act.sendingRequest(true));
  yield put(act.showProgressBar());

  const response = yield call(apiBilling.getSubscriptionStatus);
  const subscribedFeature: any[] = [];
  forEach(response.data, (feature) => {
    if (
      feature.is_active &&
      (feature.is_subscribing || !feature.is_subscribing)
    )
      subscribedFeature.push(feature);
  });
  yield put(actOutletList.setFeatureSubscriptionStatus(subscribedFeature));
  yield put(act.sendingRequest(false));
  yield put(act.hideProgressBar());
}

export function* verifyAddOutletFlow(request) {
  yield put(act.sendingRequest(true));
  yield put(actOutletList.disablePayButton());
  yield put(actOutletList.setOnLoading(true));

  const { billingData } = request.payload;
  let response: any = {};
  try {
    response = yield call(apiBilling.verifyBilling, billingData);
  } catch (e) {
    assertErrorHasData(e);
    yield put(actOutletList.setOnLoading(false));
    yield put(actOutletList.enablePayButton());
    if (e.data) {
      yield put(actNotif.error(e.data.message));
    } else {
      yield put(actOutletList.hideAddOutletModal());
      yield put(actOutletList.showPaymentGatewayError());
    }
  } finally {
    yield put(act.sendingRequest(false));
    if (response.data) {
      yield put(
        actOutletList.setPaymentButtonMessage('Redirecting to Veritrans')
      );
      window.location.replace(response.data.data.redirect_url);
    }
  }
}

export function* isCloning() {
  const outlet: any = {};
  try {
    const response = yield call(apiCloneOutlet.getProgress);
    if (Object.entries(response.data).length > 0) {
      outlet.source_clone_id = response.data.extra_info_map.source_clone_id;
      outlet.destination_outlet_id =
        response.data.extra_info_map.destination_clone_id;
      outlet.source_clone_name = response.data.extra_info_map.source_clone_name;
      outlet.destination_outlet_name =
        response.data.extra_info_map.destination_clone_name;

      if (response.data.success) {
        yield put(actOutletList.setCloneSuccess(outlet));
      } else if (!response.data.success && response.data.success != null) {
        yield put(actOutletList.setCloneFailed(outlet));
      }

      yield put(actOutletList.updateCloningProgress(response));

      // no need to show clone progress when no response
      yield put(actOutletList.showCloneOutletProgress(outlet));
    }
  } catch (e) {
    clevertapOutletsList.cloneOutletFailed();
    yield put(actOutletList.setCloneFailed(outlet));
    yield put(actOutletList.showCloneOutletProgress(outlet));
  }
}

export function* submitCloneOutlet(param) {
  const { outlet } = param.payload;
  try {
    const response = yield call(apiCloneOutlet.submitCloneOutlet, outlet);
    if (response.data.success) {
      yield put(actOutletList.setCloneSuccess(outlet));
    } else if (!response.data.success && response.data.success != null) {
      yield put(actOutletList.setCloneFailed(outlet));
      const failedOutlet: any = {};
      failedOutlet.source_clone_id = outlet.source_outlet_id;
      failedOutlet.destination_outlet_id = outlet.destination_outlet_id;
      failedOutlet.source_clone_name = outlet.source_outlet_name;
      failedOutlet.destination_outlet_name = outlet.destination_outlet_name;
      yield put(actOutletList.showCloneOutletProgress(failedOutlet));
    }
    yield put(actOutletList.updateCloningProgress(response));
  } catch (error) {
    assertErrorHasMessage(error);
    if (error.message === '0063-0002') {
      yield put(actOutletList.resetCloneResult());
      yield put(actOutletList.resetCloningProgress());
      yield put(actOutletList.hideCloneOutletProgress());
      return yield put(
        actNotif.error(
          'Still in process of deleting item from all outlets. Please try again later.'
        )
      );
    }
    clevertapOutletsList.cloneOutletFailed();
    yield put(actOutletList.setCloneFailed(outlet));
    yield put(actOutletList.showCloneOutletProgress(outlet));
  }
}

export function* deleteCacheOnCloneSuccess() {
  try {
    const res = yield call(apiProfile.getProfile);
    yield put(actOutletList.setProfile(res.data));
    yield call(
      fillSearchFormFromOutletIds,
      getSelectedOutletIdFromLocalStorage()
    );
    yield call(apiCloneOutlet.deleteCache);
    yield put(actOutletList.hideCloneOutletProgress());
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  }
}

export function* checkCloningOnProgress() {
  try {
    const isEligible = yield call(isCloningEligible);
    if (!isEligible) return;

    // hide clone notification
    yield put(actOutletList.hideCloneOutletNofication());

    const response = yield call(apiCloneOutlet.getProgress);
    // if there is no clone activity
    if (!response.data) {
      yield put(actOutletList.hideCloneOutletNofication());
      yield put(actNotif.reset());
      return;
    }

    if (response.data.extra_info_map.source_clone_id) {
      // get current outlet
      const outletList = yield select(getSelectedOutlet);
      if (!outletList === true || outletList.length === 0) return;
      const selectedOutlet = outletList[0];
      // if current outlet === source clone outlet
      if (
        response.data.extra_info_map.source_clone_id ===
        selectedOutlet.toString()
      ) {
        const cloneOutlet = {
          source: response.data.extra_info_map.source_clone_name,
          destination: response.data.extra_info_map.destination_clone_name,
          success: response.data.success,
        };
        if (response.data.success === null) {
          // if any in progress cloning
          yield onCloningInProgress(cloneOutlet);
        } else if (response.data.success === true) {
          // if cloning completed
          yield onCloningCompleted(cloneOutlet);
        } else if (response.data.success === false) {
          // if any in cloning error
          yield onCloningError(cloneOutlet);
        }
      } else {
        yield put(actOutletList.hideCloneOutletNofication());
        yield put(actNotif.reset());
      }
    }
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  }
}

export function* checkCloneOutletProgressChanged(param) {
  const outletList = yield select(getSelectedOutlet);
  if (!outletList === true || outletList.length === 0) return;
  const selectedOutlet = outletList[0];
  if (selectedOutlet.toString() === param.payload.data.source_outlet_id) {
    yield checkCloningOnProgress();
  }
}

export default function* saga() {
  yield takeLatest(OUTLET_PAGE_LIST_PAGE_REQUEST, pageRequest);
  yield fork(clickOutletRow);
  yield fork(clickOutletDetailRow);
  yield fork(clickCancel);
  yield fork(clickCreateOutlet);
  yield fork(clickAddOutlet);
  yield takeLatest(OUTLET_PAGE_LIST_SEARCH, search);
  yield takeEvery(OUTLET_PAGE_LIST_SUBMIT_FORM_UPDATE, submitFormUpdate);
  yield takeEvery(OUTLET_PAGE_LIST_SUBMIT_FORM_CREATE, submitFormCreate);
  yield takeEvery(OUTLET_PAGE_LIST_ASK_REACTIVATE_OUTLET, askReactivateOutlet);
  yield takeEvery(
    OUTLET_PAGE_LIST_CONFIRM_REACTIVATE_OUTLET,
    confirmReactivateOutlet
  );
  yield takeEvery(
    OUTLET_PAGE_LIST_CANCEL_REACTIVATE_OUTLET,
    cancelReactivateOutlet
  );
  yield takeLatest(OUTLET_PAGE_LIST_GET_OUTLET_PRICE, outletPriceFlow);
  yield takeLatest(
    OUTLET_PAGE_LIST_GET_SUBSCRIPTION_STATUS,
    featureSubscriptionStatusFlow
  );
  yield takeLatest(
    OUTLET_PAGE_LIST_VERIFY_ADD_OUTLET_BILLING_DATA,
    verifyAddOutletFlow
  );
  yield takeLatest(OUTLET_PAGE_LIST_GET_BILLING, getBillingsFlow);
  yield takeLatest(
    OUTLET_PAGE_ASK_REACTIVATE_OUTLET_WITHOUT_PAY,
    askReactivateOutletWithoutPay
  );
  yield takeLatest(
    OUTLET_PAGE_CONFIRM_REACTIVATE_OUTLET_WITHOUT_PAY,
    confirmReactivateOutletWithoutPayFlow
  );
  yield takeLatest(OUTLET_PAGES_IS_STILL_CLONING, isCloning);
  yield takeLatest(OUTLET_PAGES_SUBMIT_CLONE_OUTLET, submitCloneOutlet);
  yield takeLatest(
    OUTLET_PAGES_CHECK_CLONE_ON_PROGRESS,
    checkCloningOnProgress
  );
  yield takeLatest(
    OUTLET_PAGES_CLONE_SUCCESS_AND_CONFIRMED,
    onCloningCompletedAndConfirmed
  );
  yield takeLatest(
    OUTLET_PAGES_DELETE_CLONE_CACHE_ON_SUCCESS,
    deleteCacheOnCloneSuccess
  );
  yield takeLatest(
    OUTLET_PAGES_CHECK_CLONE_PROGRESS_CHANGED,
    checkCloneOutletProgressChanged
  );

  yield takeLatest(
    OUTLET_PAGES_POPULATE_CLONE_OUTLET_DATA,
    populateCloneOutletData
  );
  yield takeLatest(OUTLET_PAGES_ON_CHANGE_STATUS, onChangeStatusFlow);
  yield takeLatest(
    OUTLET_PAGES_GOBIZ_OUTLET_LINKING_CONFIRMED,
    processGoBizOutletLinking
  );
  yield takeLatest(
    OUTLET_PAGES_PREPARE_GOBIZ_OUTLET_LINKING,
    prepareGoBizOutletLinking
  );
  yield takeLatest(OUTLET_PAGE_LIST_UNLINK_GOBIZ_MODAL, unlinkGoBizLinking);
  yield takeLatest(
    OUTLET_PAGES_GOBIZ_OUTLET_RECOMMENDATION_ON_CHANGE_OUTLET,
    unlinkCurrentGoBizLinkingAndRedirect
  );
}
